import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './autoComplete.module.scss';
import useAriaDismiss from '../hooks/useAriaDismiss';

// Option type
export type TpAutoCompleteOption = {
  id: string;
  label: string;
};

// Class names that can be customized by the consumer
export type TpAutoCompleteClassNames = {
  container?: string;
  input?: string;
  option?: string;
  optionHighlighted?: string;
  badge?: string;
  inputGroup?: string;
  vertical?: string;
  inputField?: string;
  badgeRemove?: string;
  dropdown?: string;
  arrowButton?: string;
};

interface TpAutoCompleteProps {
  // All available options (should be provided/updated by parent so multiple components can share)
  options: TpAutoCompleteOption[];
  // Currently selected values (order matters).
  // The control expects the parent to manage the value state. When onChange is called, the parent should update the value prop accordingly!!!
  value: TpAutoCompleteOption[];
  // Called when selection list changes (add/remove/reorder)
  onChange: (next: TpAutoCompleteOption[]) => void;
  // Optional render for options
  renderOption?: (opt: TpAutoCompleteOption) => React.ReactNode;
  // Optional custom classnames
  classNames?: TpAutoCompleteClassNames;
  // Show arrow button to reveal all options
  allowShowAll?: boolean;
  // When true, badges stack vertically and each takes a full line
  vertical?: boolean;
  // optional placeholder text
  placeholder?: string;
  // optional id for accessibility
  id?: string;
}

/**
 * Auto-complete component that allows selecting multiple options from a list.
 * This component is a controlled component: the parent is responsible for managing the selected values:
 * i.e. passing the current value and updating it when onChange is called.
 */
export function AutoComplete({
  options,
  value,
  onChange,
  renderOption,
  classNames = {},
  allowShowAll = true,
  vertical = false,
  placeholder,
  id,
}: TpAutoCompleteProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLUListElement | null>(null);

  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);

  // Available options = provided options minus those already selected
  const available = options.filter((o) => !value.some((v) => v.id === o.id));

  // Suggestions based on typed text
  const suggestions =
    text.trim() === '' ? available : available.filter((o) => o.label.toLowerCase().includes(text.trim().toLowerCase()));

  useEffect(() => {
    // reset highlight when suggestion list changes
    setHighlightedIndex((idx) =>
      suggestions.length === 0 ? -1 : Math.min(idx >= 0 ? idx : 0, suggestions.length - 1),
    );
  }, [suggestions.length]);

  // Dismiss behavior handled by hook. onDismiss should commit or clear text and close.
  useAriaDismiss({
    containerRef,
    dropdownRef,
    open,
    onDismiss: () => {
      selectOrClear();
      setOpen(false);
    },
  });

  /**
   * Based on current text, either select the matching option or clear the text input.
   */
  function selectOrClear() {
    const trimmed = text.trim();
    if (trimmed.length === 0) {
      setText('');
      return;
    }
    // find exact match (case-insensitive) among available
    const match = available.find((o) => o.label.toLowerCase() === trimmed.toLowerCase());
    if (match) {
      selectOption(match);
    } else {
      setText('');
    }
  }

  /**
   * Add the given option to the selected values.
   */
  function selectOption(opt: TpAutoCompleteOption) {
    /* Should not be possible to select an already-selected option */
    if (value.some((v) => v.id === opt.id)) {
      setText('');
      return;
    }
    onChange([...value, opt]);
    setText('');
    setOpen(false);
    if (inputRef.current) inputRef.current.focus();
  }

  /**
   * Remove the given option from the selected values.
   * Does not modify the value (parent is responsible for updating the value prop)
   */
  function removeOption(opt: TpAutoCompleteOption) {
    onChange(value.filter((v) => v.id !== opt.id));
    if (inputRef.current) inputRef.current.focus();
  }

  /**
   * Handle input field keyboard typing and navigation in the suggestion list
   */
  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlightedIndex((i) => {
        const next = i + 1;
        return next >= suggestions.length ? 0 : next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlightedIndex((i) => {
        const next = i - 1;
        return next < 0 ? Math.max(0, suggestions.length - 1) : next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        selectOption(suggestions[highlightedIndex]);
      } else {
        selectOrClear();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setText('');
    } else if (e.key === 'Backspace') {
      if (text === '' && value.length > 0) {
        // remove last selected (preserve order)
        onChange(value.slice(0, value.length - 1));
      }
    }
  }

  /**
   * Toggle showing the list of options
   * Clear the current text and focus the input for keyboard navigation.
   */
  function toggleShowAll() {
    if (!allowShowAll) return;
    setOpen((o) => {
      const next = !o;
      if (next && inputRef.current) {
        // focus and clear filter to show all
        setText('');
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      return next;
    });
  }

  // Build class names
  // prefer consumer-provided class names; otherwise fall back to local module names
  // Always apply base module classes and append any consumer-provided classes so
  // consumers can override specific properties via specificity or later rules.
  const cls = {
    container: `${styles.container} ${classNames.container || ''}`.trim(),
    input: `${styles.input} ${classNames.input || ''}`.trim(),
    badge: `${styles.badge} ${classNames.badge || ''}`.trim(),
    badgeRemove: `${styles.badgeRemove} ${classNames.badgeRemove || ''}`.trim(),
    inputField: `${styles.inputField} ${classNames.inputField || ''}`.trim(),
    arrowButton: `${styles.arrowButton} ${classNames.arrowButton || ''}`.trim(),
    inputGroup: `${styles.inputGroup} ${classNames.inputGroup || ''}`.trim(),
    vertical: `${vertical ? styles.vertical : ''} ${classNames.vertical || ''}`.trim(),
    dropdown: `${styles.dropdown} ${classNames.dropdown || ''}`.trim(),
    option: `${styles.option} ${classNames.option || ''}`.trim(),
    optionHighlighted: `${styles.optionHighlighted} ${classNames.optionHighlighted || ''}`.trim(),
  };

  return (
    <div ref={containerRef} id={id} className={cls.container}>
      <div
        className={`${cls.input} ${cls.vertical}`.trim()}
        onClick={() => {
          /* Clicking the container should focus the input */
          inputRef.current?.focus();
        }}
      >
        {value.map((v) => (
          <span key={v.id} className={cls.badge}>
            <span>{v.label}</span>
            <button
              type="button"
              aria-label={`Remove ${v.label}`}
              onClick={(e) => {
                e.stopPropagation();
                removeOption(v);
              }}
              className={cls.badgeRemove}
            >
              <FontAwesomeIcon icon="xmark" />
            </button>
          </span>
        ))}

        <div className={cls.inputGroup}>
          <input
            ref={inputRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setOpen(true);
            }}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className={cls.inputField}
          />

          {allowShowAll && (
            <button
              type="button"
              aria-label="Show options"
              onClick={(e) => {
                e.stopPropagation();
                toggleShowAll();
              }}
              className={cls.arrowButton}
            >
              <FontAwesomeIcon icon={['fas', 'chevron-down']} />
            </button>
          )}
        </div>
      </div>

      {open && suggestions.length > 0 && (
        <ul className={cls.dropdown}>
          {suggestions.map((opt, idx) => {
            const highlighted = idx === highlightedIndex;
            return (
              <li
                key={opt.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectOption(opt);
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
                className={`${cls.option} ${highlighted ? cls.optionHighlighted : ''}`}
              >
                {renderOption ? renderOption(opt) : opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
